import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/data-table/DataTable";
import type { ColumnDef } from "@/components/data-table/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Mail, Plus, Trash2, Copy, Check, Link2 } from "lucide-react";

interface SalesInvitation {
  id: string;
  email: string;
  invitationToken: string;
  role: string;
  companyName: string;
  expiresAt: string;
  isConsumed: boolean;
  consumedAt: string | null;
  createdAt: string;
  consumedUser: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
  } | null;
}

interface InvitationsResponse {
  data: SalesInvitation[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

async function fetchInvitations(page: number): Promise<InvitationsResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: "10" });
  const response = await fetch(`/api/sales/invitations?${params}`, { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch invitations");
  return response.json();
}

async function createInvitation(companyName: string) {
  const response = await fetch("/api/sales/invitations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ companyName }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to create invitation");
  }
  return response.json();
}

async function deleteInvitation(id: string) {
  const response = await fetch(`/api/sales/invitations/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to delete invitation");
  }
  return response.json();
}

export default function SalesInvitations() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvitationId, setSelectedInvitationId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [createdInvitation, setCreatedInvitation] = useState<SalesInvitation | null>(null);
  const [copied, setCopied] = useState(false);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["/api/sales/invitations", page],
    queryFn: () => fetchInvitations(page),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => createInvitation(name),
    onSuccess: (data) => {
      setCreatedInvitation(data.invitation);
      queryClient.invalidateQueries({ queryKey: ["/api/sales/invitations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sales/stats"] });
      toast({ title: "Invitation created", description: "The invitation token has been generated successfully." });
    },
    onError: (error: Error) => {
      toast({ title: "Creation failed", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sales/invitations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sales/stats"] });
      toast({ title: "Invitation deleted", description: "The invitation has been removed successfully." });
      setDeleteDialogOpen(false);
      setSelectedInvitationId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Deletion failed", description: error.message, variant: "destructive" });
    },
  });

  const handleCreate = () => {
    if (!companyName) {
      toast({ title: "Company name required", description: "Please enter a company name.", variant: "destructive" });
      return;
    }
    createMutation.mutate(companyName);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCompanyName("");
    setCreatedInvitation(null);
    setCopied(false);
  };

  const handleDelete = (invitationId: string) => {
    setSelectedInvitationId(invitationId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedInvitationId) deleteMutation.mutate(selectedInvitationId);
  };

  const handleViewInvitation = (invitation: SalesInvitation) => {
    setCreatedInvitation(invitation);
    setCopied(false);
    setCreateDialogOpen(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Copied!", description: "Invitation link copied to clipboard." });
  };

  const getInviteLink = (token: string) => `${window.location.origin}/onboarding?token=${token}`;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

  const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date();

  const getStatusStyle = (invitation: SalesInvitation) => {
    if (invitation.isConsumed) return { bg: "bg-emerald-50", text: "text-emerald-600", dot: "bg-emerald-500" };
    if (isExpired(invitation.expiresAt)) return { bg: "bg-red-50", text: "text-red-600", dot: "bg-red-500" };
    return { bg: "bg-amber-50", text: "text-amber-600", dot: "bg-amber-500" };
  };

  const getStatusLabel = (invitation: SalesInvitation) => {
    if (invitation.isConsumed) return "Joined";
    if (isExpired(invitation.expiresAt)) return "Expired";
    return "Pending";
  };

  const columns: ColumnDef<SalesInvitation>[] = [
    {
      id: "company",
      header: "Company",
      cell: (invitation) => <span className="font-semibold text-slate-900">{invitation.companyName}</span>,
    },
    {
      id: "status",
      header: "Status",
      cell: (invitation) => {
        const ss = getStatusStyle(invitation);
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${ss.bg} ${ss.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
            {getStatusLabel(invitation)}
          </span>
        );
      },
    },
    {
      id: "joinedAs",
      header: "Joined As",
      cell: (invitation) =>
        invitation.consumedUser ? (
          <div className="flex flex-col">
            <span className="font-semibold text-slate-900">
              {invitation.consumedUser.firstName && invitation.consumedUser.lastName
                ? `${invitation.consumedUser.firstName} ${invitation.consumedUser.lastName}`
                : invitation.consumedUser.email}
            </span>
            {invitation.consumedAt && (
              <span className="text-xs text-slate-400">{`Joined: ${formatDate(invitation.consumedAt)}`}</span>
            )}
          </div>
        ) : (
          <span className="text-slate-400 text-sm">-</span>
        ),
    },
    {
      id: "created",
      header: "Created",
      cell: (invitation) => <span className="text-sm text-slate-500">{formatDate(invitation.createdAt)}</span>,
    },
    {
      id: "expires",
      header: "Expires",
      cell: (invitation) => <span className="text-sm text-slate-500">{formatDate(invitation.expiresAt)}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      align: "right",
      cell: (invitation) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => handleViewInvitation(invitation)} title="View invitation">
            <Link2 className="h-4 w-4 text-primary" />
          </Button>
          {!invitation.isConsumed && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={() => handleDelete(invitation.id)}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">My Invitations</h1>
          <p className="text-slate-500 mt-2">Create and manage your own company onboarding invitations</p>
        </div>
        <Dialog
          open={createDialogOpen}
          onOpenChange={(open) => (open ? setCreateDialogOpen(true) : handleCloseCreateDialog())}
        >
          <DialogTrigger asChild>
            <Button className="rounded-2xl bg-primary hover:bg-primary/80 font-bold">
              <Plus className="h-4 w-4 mr-2" />
              Create Invitation
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] rounded-3xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-slate-900">
                {createdInvitation ? "Invitation Details" : "Create Company Onboarding Invite"}
              </DialogTitle>
              <DialogDescription className="text-slate-500">
                {createdInvitation
                  ? "Share this invitation link with the company admin to onboard their company."
                  : "Generate an invitation link for a company admin to create their workspace."}
              </DialogDescription>
            </DialogHeader>
            {!createdInvitation ? (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-slate-700">Company Name</Label>
                  <Input
                    id="companyName"
                    type="text"
                    placeholder="e.g. Malawi Sun Energy Ltd."
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="bg-slate-50 rounded-2xl"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-slate-700">Invitation Link</Label>
                  <div className="flex gap-2">
                    <Input value={getInviteLink(createdInvitation.invitationToken)} readOnly className="font-mono text-sm bg-slate-50 rounded-2xl" />
                    <Button size="icon" variant="outline" className="rounded-xl" onClick={() => copyToClipboard(getInviteLink(createdInvitation.invitationToken))}>
                      {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4 text-primary" />}
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Company:</span>
                    <span className="font-semibold text-slate-900">{createdInvitation.companyName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Expires:</span>
                    <span className="font-semibold text-slate-900">{formatDate(createdInvitation.expiresAt)}</span>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              {!createdInvitation ? (
                <>
                  <Button variant="outline" className="rounded-2xl" onClick={handleCloseCreateDialog}>Cancel</Button>
                  <Button onClick={handleCreate} disabled={createMutation.isPending} className="rounded-2xl bg-primary hover:bg-primary/80 font-bold">
                    {createMutation.isPending ? "Creating..." : "Create Token"}
                  </Button>
                </>
              ) : (
                <Button className="rounded-2xl" onClick={handleCloseCreateDialog}>Done</Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-5 border-b border-border/30">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">My Company Onboarding Invitations</h3>
              <p className="text-sm text-slate-500">Invitations you've created for companies to join</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <DataTable
            columns={columns}
            data={data?.data ?? []}
            getRowId={(invitation) => invitation.id}
            isLoading={isLoading}
            emptyState={{ icon: Mail, title: "No invitations found", description: "Create your first company onboarding invitation" }}
            pagination={data ? { page, pageSize: data.pageSize, total: data.total, totalPages: data.totalPages, onPageChange: setPage } : undefined}
          />
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-slate-900">Delete Invitation</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Are you sure you want to delete this invitation? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 rounded-2xl">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
